"use client"

import { useLocale, useTranslations } from "next-intl"

import { priceVariants } from "@/components/marketplace/product/price"
import { WishlistHeart } from "@/components/marketplace/product/wishlist-heart"
import type { CartLine } from "@/components/marketplace/types"
import { Cents, Chip, DiscountBadge, IconButton, Row, Stack, Surface, Text, TrashIcon, TruckFastIcon } from "@/components/ui"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { SchemeScope } from "@/components/ui/scheme-scope"
import { TileImage } from "@/components/ui/tile-image"
import { resolveSupportedLocale } from "@/i18n/locales"
import { formatDeliveryWindow } from "@/lib/helpers/delivery"
import { compareMoney, formatMoneyRounded, formatMoneyWithCode, type Money, moneyToNumber } from "@/lib/money"

interface CartLineRowProps {
  line: CartLine
  onRemove?: () => void
}

const discountPercent = (price: Money, was?: Money) => (was && compareMoney(was, price) > 0 ? Math.round((1 - moneyToNumber(price) / moneyToNumber(was)) * 100) : 0)

export const CartLineRow = ({ line, onRemove }: CartLineRowProps) => {
  const t = useTranslations("cart")
  const tm = useTranslations("marketplace")
  const locale = resolveSupportedLocale(useLocale())
  const price = priceVariants({ size: "product", weight: "semibold" })
  const percent = discountPercent(line.price, line.was)
  const arrivalWindow = line.delivery ? formatDeliveryWindow(line.delivery, locale) : null
  const media = (
    <SchemeScope scheme="light" className="rounded-8x bg-surface-subtle @lg:rounded-10x relative size-22 shrink-0 overflow-hidden @lg:size-33">
      <TileImage src={line.image} alt={line.title} fill sizes="133px" className="object-contain p-150" />
    </SchemeScope>
  )
  const titleText = (
    <Text as="span" variant="body" family="text" tone="default" className="text-fg-heading @lg:text-body-lg line-clamp-2 font-medium">
      {line.title}
    </Text>
  )

  return (
    <div className="@container">
      <Surface padding="none" className="rounded-10x border-line-faint shadow-panel @lg:rounded-12x relative border p-350 @lg:p-500" data-testid="cart-line">
        <Row gap="250" align="start" className="@lg:gap-500">
          {line.href ? (
            <LocalizedClientLink
              href={line.href}
              className="focus-visible:ring-brand rounded-8x shrink-0 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            >
              {media}
            </LocalizedClientLink>
          ) : (
            media
          )}

          <Stack gap="0" className="min-w-0 flex-1">
            <div className="pr-1200">
              {line.href ? (
                <LocalizedClientLink href={line.href} className="hover:text-fg-brand focus-visible:ring-brand rounded-4x focus-visible:ring-2 focus-visible:outline-none">
                  {titleText}
                </LocalizedClientLink>
              ) : (
                titleText
              )}
            </div>
            {line.variant && (
              <Text as="span" variant="bodySm" family="text" tone="muted">
                {line.variant}
              </Text>
            )}
            <Row gap="150" align="baseline" wrap>
              <span className={price.amount({ className: "@card:text-price-sm @lg:text-price-md" })}>
                <Cents>{formatMoneyWithCode(line.price, locale)}</Cents>
              </span>
              <span className="flex items-baseline gap-150 whitespace-nowrap">
                {line.was && <span className={price.was({ className: "text-caption-lg @lg:text-body-sm" })}>{formatMoneyRounded(line.was, locale)}</span>}
                {percent > 0 && (
                  <DiscountBadge tone="red" size="sm" className="text-caption-lg @lg:text-body-sm py-0 leading-4.5 tabular-nums @lg:leading-4.75">
                    {t("discountOff", { percent })}
                  </DiscountBadge>
                )}
              </span>
            </Row>
            {line.payments != null && line.qty === 1 && (
              <Text as="span" variant="body" family="text" numeric className="text-brand text-body-xs @lg:text-body font-medium @lg:leading-4.75">
                <Cents>{t("installmentPlan", { amount: formatMoneyWithCode(line.payments, locale), count: line.installments ?? 4 })}</Cents>
              </Text>
            )}
            {line.qty > 1 && (
              <Text as="span" variant="bodySm" family="text" tone="muted" className="mt-100">
                {t("quantity", { qty: line.qty })}
              </Text>
            )}
            {arrivalWindow && (
              <Row gap="150" align="start" className="text-fg-heading mt-125 @lg:mt-250">
                <TruckFastIcon size={15} className="mt-50 shrink-0" aria-hidden />
                <Text as="span" variant="bodySm" family="text" tone="inherit" className="min-w-0">
                  {t.rich("arrives", { window: arrivalWindow, when: (chunks) => <b className="font-semibold">{chunks}</b> })}
                </Text>
              </Row>
            )}
            <Row gap="150" wrap className="mt-125 @lg:mt-250">
              <Chip tone="nointerest" size="detail" className="@card:text-caption-lg @card:leading-4.5 rounded-full">
                {tm("product.noInterest")}
              </Chip>
              {line.freeship && (
                <Chip tone="freeship" size="detail" className="@card:text-caption-lg @card:leading-4.5">
                  {tm("product.freeShipping")}
                </Chip>
              )}
            </Row>
          </Stack>
        </Row>

        {onRemove && (
          <IconButton
            variant="ghost"
            aria-label={t("removeItem", { title: line.title })}
            className="text-fg-subtle hover:text-fg-danger absolute top-200 right-200 @lg:top-350 @lg:right-350"
            icon={<TrashIcon size={16} />}
            onClick={onRemove}
          />
        )}
        {line.productId && <WishlistHeart productId={line.productId} className="absolute right-200 bottom-200 @lg:right-350 @lg:bottom-350" />}
      </Surface>
    </div>
  )
}
